import {
  Box,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material'

import { selectTerritory } from '@/features/domain/territory'
import * as orderHelpers from '@/local/server-data/domain/helpers/order'
import { useSelector } from 'react-redux'
import { type OrderDataStatus, useController } from '../../../../../hooks/useController'
import { useTexts } from '../../../../../useTexts'
import { IncludePod } from './IncludePod'
import { NotExecutedOrdersWarning } from './NotExecutedOrdersWarning'

function RadioOption({ value, label, description, testId, disabled = false }) {
  return (
    <Stack>
      <FormControlLabel
        value={value}
        control={<Radio data-testid={testId} data-trackid={testId} />}
        title=""
        label={label}
        disabled={disabled}
      />

      <FormHelperText sx={{ ml: 4 }}>{description}</FormHelperText>
    </Stack>
  )
}

export function OrderData() {
  const texts = useTexts()
  const { rescheduleBehavior } = useSelector(selectTerritory)

  const {
    data: { selectedOrders, orderDataStatus, source },
    updateData,
  } = useController()

  const executedOrders = selectedOrders.filter(
    order =>
      orderHelpers.isOrderStepExecuted(order.orderStep, rescheduleBehavior) ||
      order.status === 'notCompleted',
  )

  const notExecutedOrdersCount = selectedOrders.length - executedOrders.length

  return (
    <Stack spacing={2}>
      <FormLabel id="order-data-picker">{texts.orderDataTitle}</FormLabel>

      {source === 'default' ? (
        <>
          <RadioGroup
            aria-labelledby="order-data-picker"
            value={orderDataStatus}
            onChange={e => updateData({ orderDataStatus: e.target.value as OrderDataStatus })}
            data-testid="order-data-picker"
            data-trackid="order-data-picker"
            sx={{ ml: 1 }}
          >
            <RadioOption
              value="configuration"
              label={texts.configurationTitle}
              description={texts.configurationDescription}
              testId="order-data-configuration"
            />
            <RadioOption
              value="execution"
              label={texts.executionTitle}
              description={texts.executionDescription}
              testId="order-data-execution"
              disabled={notExecutedOrdersCount > 0 && executedOrders.length === 0}
            />
          </RadioGroup>

          {orderDataStatus === 'execution' && (
            <Box sx={{ ml: 4 }}>
              <IncludePod />
            </Box>
          )}

          {notExecutedOrdersCount > 0 && orderDataStatus === 'execution' && (
            <NotExecutedOrdersWarning
              executedCount={executedOrders.length}
              totalCount={selectedOrders.length}
            />
          )}
        </>
      ) : (
        <>
          {source === 'configuration' && (
            <>
              <Typography variant="body1">{texts.configurationTitle}</Typography>
              <Typography variant="caption" color="text.secondary">
                {texts.configurationDescription}
              </Typography>
            </>
          )}
          {source === 'execution' && (
            <>
              <Typography variant="body1">{texts.executionTitle}</Typography>
              <Typography variant="caption" color="text.secondary">
                {texts.executionDescription}
              </Typography>
              <IncludePod />
            </>
          )}
        </>
      )}
    </Stack>
  )
}
